import {
  DAILY_MISSION_COUNT,
  MISSION_PACK_THRESHOLD,
  isMissionId,
  missionDef,
  type MissionId,
} from './catalog'
import type { DuelMissionMetrics } from './metrics'
import {
  normalizeRankedDaily,
  type RankedDailyState,
} from './ranked'
import { runnerRows, type RunnerMissionRow } from './runner'

export type DailyMissionRow = {
  id: MissionId
  progress: number
  target: number
  done: boolean
}

export type DailyMissionsSnapshot = {
  day: string
  rows: DailyMissionRow[]
  completed: number
  packGranted: boolean
  collected: MissionId[]
  railClaimed: number
  /** Ranked Quick Matches finished today. Steps 1–9 pay coins; 7 grants one pack and 10 grants two. */
  rankedPlayed: number
  /** Highest ranked step whose reward was already celebrated. */
  rankedCollected: number
  rankedPackGranted: boolean
  /** Shop pack from ranked step 7. */
  rankedStep7PackGranted: boolean
  newlyCompleted?: MissionId[]
  /** Ranked step reached by this update, when one was. */
  newlyRanked?: number
  /** Queue-race goals for this UTC day. Not part of the rolled board of 10. */
  runner: RunnerMissionRow[]
  coins?: number
  packs?: Record<string, number>
}

export const EMPTY_DAILY_MISSIONS: DailyMissionsSnapshot = {
  day: '',
  rows: [],
  completed: 0,
  packGranted: false,
  collected: [],
  railClaimed: 0,
  rankedPlayed: 0,
  rankedCollected: 0,
  rankedPackGranted: false,
  rankedStep7PackGranted: false,
  runner: runnerRows(0, 0, []),
}

function cloneRow(row: DailyMissionRow): DailyMissionRow {
  return {
    id: row.id,
    progress: row.progress,
    target: row.target,
    done: row.done,
  }
}

/** Build rows for a freshly rolled id list. */
export function rowsFromIds(ids: readonly MissionId[]): DailyMissionRow[] {
  const rows: DailyMissionRow[] = []
  for (const id of ids) {
    const def = missionDef(id)
    if (!def) continue
    rows.push({ id, progress: 0, target: def.target, done: false })
  }
  return rows
}

/**
 * Apply one event's metrics onto the day's board. Returns a new row list
 * and the ids that flipped to done in this call.
 */
export function applyMetricsToMissions(
  rows: readonly DailyMissionRow[],
  metrics: DuelMissionMetrics,
): { rows: DailyMissionRow[]; newlyCompleted: MissionId[] } {
  const next: DailyMissionRow[] = []
  const newlyCompleted: MissionId[] = []
  for (const row of rows) {
    const copy = cloneRow(row)
    if (!copy.done) {
      const def = missionDef(copy.id)
      if (def) {
        const delta = metrics[def.metric] ?? 0
        if (def.kind === 'max') {
          copy.progress = Math.max(copy.progress, delta)
        } else if (delta > 0) {
          copy.progress = Math.min(copy.target, copy.progress + delta)
        }
        if (copy.progress >= copy.target) {
          copy.progress = copy.target
          copy.done = true
          newlyCompleted.push(copy.id)
        }
      }
    }
    next.push(copy)
  }
  return { rows: next, newlyCompleted }
}

function coerceJson(raw: unknown): unknown {
  if (typeof raw !== 'string') return raw
  try {
    return JSON.parse(raw)
  } catch {
    return raw
  }
}

export function parseMissionRows(raw: unknown): DailyMissionRow[] | null {
  const value = coerceJson(raw)
  if (!Array.isArray(value) || value.length === 0) return null
  const rows: DailyMissionRow[] = []
  for (const item of value) {
    if (!item || typeof item !== 'object') return null
    const rec = item as Record<string, unknown>
    if (!isMissionId(rec.id)) continue
    const def = missionDef(rec.id)
    if (!def) return null
    const progress = typeof rec.progress === 'number' && Number.isFinite(rec.progress)
      ? Math.max(0, Math.trunc(rec.progress))
      : 0
    rows.push({
      id: rec.id,
      progress: Math.min(def.target, progress),
      target: def.target,
      done: rec.done === true || progress >= def.target,
    })
  }
  return rows.length >= DAILY_MISSION_COUNT ? rows : null
}

export function parseCollected(raw: unknown): MissionId[] {
  const value = coerceJson(raw)
  if (!Array.isArray(value)) return []
  const out: MissionId[] = []
  const seen = new Set<string>()
  for (const item of value) {
    if (!isMissionId(item) || seen.has(item)) continue
    seen.add(item)
    out.push(item)
  }
  return out
}

export function snapshotFromRows(
  day: string,
  rows: DailyMissionRow[],
  packGranted: boolean,
  collected: MissionId[],
  railClaimed = 0,
  ranked: RankedDailyState = {
    played: 0,
    collected: 0,
    packGranted: false,
    midPackGranted: false,
  },
  runner: RunnerMissionRow[] = runnerRows(0, 0, []),
): DailyMissionsSnapshot {
  const completed = rows.filter((row) => row.done).length
  const allowed = new Set(rows.filter((row) => row.done).map((row) => row.id))
  const claimed = Number.isFinite(railClaimed)
    ? Math.max(0, Math.trunc(railClaimed))
    : 0
  const rankedState = normalizeRankedDaily(
    ranked.played,
    ranked.collected,
    ranked.packGranted,
    ranked.midPackGranted,
  )
  return {
    day,
    rows,
    completed,
    packGranted,
    collected: collected.filter((id) => allowed.has(id)),
    railClaimed: Math.min(claimed, completed, MISSION_PACK_THRESHOLD),
    rankedPlayed: rankedState.played,
    rankedCollected: rankedState.collected,
    rankedPackGranted: rankedState.packGranted,
    rankedStep7PackGranted: rankedState.midPackGranted,
    runner,
  }
}
